/**
 * Chat store — composition of feature slices (Phase 6.A.3).
 *
 * Replaces v1's `ConversationContext.tsx` (1576 LoC monolith) with five
 * independent Zustand slices combined via the canonical slice pattern
 * (https://docs.pmnd.rs/zustand/guides/slices-pattern).
 *
 * Slice surface:
 *   - conversationsSlice → sidebar + active conversation lifecycle
 *   - messagesSlice      → per-conversation messages + streaming state
 *   - reasoningSlice     → Jan 2026 live reasoning chain (per query_id)
 *   - interruptedSlice   → Jan 2026 retry UI for WS-drop-mid-query
 *   - composerSlice      → composer draft (current conv)
 *
 * All actions take `conversationId` explicitly — no implicit "current
 * conversation". Selectors that need the current conversation derive it
 * via `useCurrentConversationId()` (see hooks/).
 */

import { getSession } from "@ando/core/auth";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { ChatConversation, ConversationConfig, MessageView } from "./types";

import { type ComposerSlice, createComposerSlice } from "./composerSlice";
import { type ConversationsSlice, createConversationsSlice } from "./conversationsSlice";
import { type InterruptedSlice, createInterruptedSlice } from "./interruptedSlice";
import { type MessagesSlice, type StreamState, createMessagesSlice } from "./messagesSlice";
import { type ReasoningSlice, createReasoningSlice } from "./reasoningSlice";

export type ChatStore = ConversationsSlice &
  MessagesSlice &
  ReasoningSlice &
  InterruptedSlice &
  ComposerSlice;

/**
 * Persisted slice of the conversation map (Jun 17, 2026).
 *
 * Only the composer **config** survives a reload — every other field
 * (messages, streaming state, error flags, replay-in-progress, etc.) is
 * re-hydrated from the BE (TanStack Query refetch on mount) or arrives
 * fresh via WebSocket. Persisting more would either duplicate the BE
 * state-of-truth or freeze mid-stream UI in place.
 *
 * Why this exists:
 *   Antonio (Brini Legnami, 17 Jun 2026 10:20) selected
 *   ``focus: "structured"`` on Q1 → got a SQL-driven answer with 619
 *   rows. Reloaded the tab between Q1 and Q2. Q2 fired with
 *   ``focus: "standard"`` (DEFAULT_CONFIG reset) → agent fell back to
 *   ``search_and_answer`` and asked him to attach an Excel. The user
 *   read this as "Ando didn't respond". Persisting ``config`` keeps the
 *   toggle pinned to the conversation across reloads, matching the
 *   mental model (the "Strutturati" chip stays orange after refresh).
 */
type PersistedConversation = { config: ConversationConfig };
type PersistedSnapshot = {
  conversations: Record<string, PersistedConversation>;
  orderedIds: readonly string[];
  currentId: string | null;
  /**
   * User_id this snapshot was captured for (Jun 26, 2026). When the
   * active session belongs to a DIFFERENT user on rehydrate (impersonation,
   * account-switch, logout→login as another account on the same browser),
   * the persisted ``currentId`` would still point at the previous user's
   * conversation. Sending a query against that id leaks the response to
   * the other account's WebSocket subscription and pollutes their history
   * (CONVERSATION_NOT_FOUND incident, Jun 26 2026).
   *
   * Nullable for backward compat: snapshots written before this field
   * landed have ``owner === undefined``; ``merge`` treats them as a
   * trusted-but-unattributed snapshot and adopts the current session.
   */
  owner?: string | null;
};

/**
 * Read the active session ``user_id`` without going through React. Used
 * inside the Zustand persist ``partialize`` / ``merge`` hooks (which run
 * outside the React tree). Returns ``null`` when the session hasn't been
 * hydrated yet — ``merge`` treats that as "skip the owner gate this
 * boot" so a cold-start with an empty session never wipes a fresh store.
 *
 * Guarded with try/catch so a transient auth-storage error never
 * prevents the chat store from hydrating (worst case: the gate skips
 * and the BE security check (``ConversationService.verify_owner``)
 * still rejects cross-user access).
 */
function _activeSessionUserId(): string | null {
  try {
    return getSession()?.user_id ?? null;
  } catch {
    return null;
  }
}

export const useChatStore = create<ChatStore>()(
  persist(
    (...args) => ({
      ...createConversationsSlice(...args),
      ...createMessagesSlice(...args),
      ...createReasoningSlice(...args),
      ...createInterruptedSlice(...args),
      ...createComposerSlice(...args),
    }),
    {
      name: "ando.chat.v1",
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // Persist ONLY the per-conversation composer config + sidebar
      // order + current selection. Messages/streaming/reasoning/
      // interrupted/composer-draft are intentionally excluded — they
      // either belong to the BE (messages: rehydrated via TanStack on
      // mount) or to the live WS turn (streaming, reasoning).
      partialize: (s): PersistedSnapshot => ({
        conversations: Object.fromEntries(
          Object.entries(s.conversations).map(([id, conv]) => [
            id,
            { config: conv.config },
          ]),
        ),
        orderedIds: s.orderedIds,
        currentId: s.currentId,
        owner: _activeSessionUserId(),
      }),
      // Re-hydrate by GRAFTING the persisted ``config`` onto whatever
      // skeleton the in-memory store already holds for each id. Never
      // overwrite live fields (messages, streaming, error, etc.) — they
      // re-arrive from the BE / WS within the next render cycle and
      // would otherwise blink to ``undefined`` for a few hundred ms.
      merge: (persistedUnknown, current): ChatStore => {
        const p = persistedUnknown as PersistedSnapshot | undefined;
        if (!p) return current;

        // Owner gate (Jun 26, 2026). If the snapshot was captured for a
        // DIFFERENT user than the one now in session, drop the whole
        // persisted slice — keep only the in-memory skeleton. This
        // prevents the cross-user CONVERSATION_NOT_FOUND surge that
        // arises when the FE replays the prior owner's ``currentId``
        // against the new owner's WS connection.
        //
        // Skip the gate when EITHER side has a null user — the absent
        // info means we can't prove a mismatch (cold boot before the
        // session arrives, or a legacy snapshot from before this field
        // landed). Adopting the snapshot in those cases preserves
        // backward compat without weakening the cross-user guarantee
        // (the security check still runs server-side via
        // ``ConversationService.verify_owner``).
        const sessionUser = _activeSessionUserId();
        if (p.owner && sessionUser && p.owner !== sessionUser) {
          if (typeof console !== "undefined") {
            console.info(
              "[chatStore] persisted snapshot owner mismatch — dropping " +
                "stale state (was user=%s, now=%s)",
              p.owner.slice(0, 8),
              sessionUser.slice(0, 8),
            );
          }
          return current;
        }

        const next: Record<string, ChatConversation> = { ...current.conversations };
        for (const [id, snap] of Object.entries(p.conversations)) {
          const existing = next[id];
          if (existing) {
            next[id] = { ...existing, config: { ...existing.config, ...snap.config } };
          } else {
            // Synthetic placeholder — TanStack will rehydrate every
            // BE-canonical field (title, user_id, updated_at, queries)
            // on the next ``getConversation`` fetch. The empty strings
            // are immediately overwritten by ``hydrateConversations``;
            // they exist only so the ChatConversation shape stays
            // satisfied for the brief window between persist-rehydrate
            // and BE refetch.
            next[id] = {
              id,
              title: "",
              user_id: "",
              updated_at: "",
              queries: [],
              replayInProgress: false,
              config: snap.config,
            };
          }
        }
        return {
          ...current,
          conversations: next,
          orderedIds: [...p.orderedIds],
          currentId: p.currentId,
        };
      },
    },
  ),
);

// ─── Selectors ────────────────────────────────────────────────────────────

export const selectComposerDraft = (s: ChatStore): string => s.composerDraft;

export const selectCurrentConversationId = (s: ChatStore): string | null => s.currentId;

export const selectCurrentConversation = (s: ChatStore) => {
  if (!s.currentId) return null;
  return s.conversations[s.currentId] ?? null;
};

// Frozen empty array singleton — selectors return the SAME reference when
// there are no messages/stream for the requested conversation, so Zustand's
// strict-equality subscriber check doesn't trigger a re-render every cycle
// (and the "Maximum update depth exceeded" cascade that follows).
const EMPTY_MESSAGES: readonly MessageView[] = Object.freeze([]);

export const selectMessagesFor =
  (conversationId: string | null) =>
  (s: ChatStore): MessageView[] => {
    if (!conversationId) return EMPTY_MESSAGES as MessageView[];
    return s.messagesByConversation[conversationId] ?? (EMPTY_MESSAGES as MessageView[]);
  };

export const selectStreamingFor =
  (conversationId: string | null) =>
  (s: ChatStore): StreamState | null => {
    if (!conversationId) return null;
    return s.streamingByConversation[conversationId] ?? null;
  };

export const selectIsStreamingFor =
  (conversationId: string | null) =>
  (s: ChatStore): boolean => {
    if (!conversationId) return false;
    return s.streamingByConversation[conversationId]?.isStreaming ?? false;
  };

/**
 * Latest reasoning label for a conversation (drives the
 * `<StreamingIndicator>` blob-tier copy). Null when no `reasoning_hop`
 * event has arrived yet or once streaming completes.
 */
export const selectReasoningLabelFor =
  (conversationId: string | null) =>
  (s: ChatStore): string | null => {
    if (!conversationId) return null;
    const stream = s.streamingByConversation[conversationId];
    if (stream?.cancellationLabel) return stream.cancellationLabel;
    if (stream?.currentReasoningLabel) return stream.currentReasoningLabel;
    const status = stream?.status?.trim();
    if (!status) return null;
    if (/querying enterprise database/i.test(status)) return "Interrogo i dati aziendali…";
    if (/generating answer/i.test(status)) return "Compongo la risposta…";
    if (/processing/i.test(status)) return "Analizzo la richiesta…";
    return null;
  };

/**
 * Currently-streaming `query_id` for a conversation, or `null` when no
 * stream is active. Drives `useThinkingHops` lookups into the
 * `liveReasoning` slice (which is keyed by query_id, not conversation).
 */
export const selectQueryIdFor =
  (conversationId: string | null) =>
  (s: ChatStore): string | null => {
    if (!conversationId) return null;
    return s.streamingByConversation[conversationId]?.queryId ?? null;
  };

/**
 * `isAnswering` flag — true once the first answer token of the active
 * query has landed. The `<ThinkingChain>` reads this to swap from hop
 * list to a thin progress bar.
 */
export const selectIsAnsweringFor =
  (conversationId: string | null) =>
  (s: ChatStore): boolean => {
    if (!conversationId) return false;
    return s.streamingByConversation[conversationId]?.isAnswering ?? false;
  };

/**
 * Current-conversation convenience selectors. Sugar over
 * `selectMessagesFor(s.currentId)(s)` to avoid double-call boilerplate
 * at the call site — they return what the chat pane actually wants to
 * render right now.
 */
export const selectMessages = (s: ChatStore): MessageView[] => selectMessagesFor(s.currentId)(s);

/**
 * Pin-point selector for a single message — used by surfaces that only
 * care about ONE specific message (e.g. ``SourceRailSurface`` reading
 * the message identified by the ``?msg=<id>`` search param).
 *
 * Returns ``null`` when the id isn't found. The function form lets
 * consumers ``useChatStore(selectMessageById(messageId))`` and re-render
 * ONLY when that specific message changes — avoids the storm of
 * re-renders that ``selectMessages`` triggers on every WS token during
 * streaming (the full array gets a new reference per chunk).
 */
export const selectMessageById =
  (messageId: string | null) =>
  (s: ChatStore): MessageView | null => {
    if (!messageId) return null;
    const convs = s.messagesByConversation;
    for (const list of Object.values(convs)) {
      const hit = list.find((m) => m.id === messageId);
      if (hit) return hit;
    }
    return null;
  };

export const selectStreaming = (s: ChatStore): boolean => selectIsStreamingFor(s.currentId)(s);

/** Re-export for module-level discoverability. */
export type { StreamState };

/** Back-compat type alias used by some consumers. */
export type ChatState = ChatStore;
